import { Component, OnInit } from '@angular/core';
import { NotificationService } from '../../../_core/_services/notification.service';
import { ComplianceAdminService } from '../../../_core/_services/_compliance/compliance-admin.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';
import { ModalCleanupService } from '../../../_core/_services/modal-cleanup.service';

@Component({
  selector: 'app-admin-policy-documents',
  standalone: false,
  templateUrl: './admin-policy-documents.html',
  styleUrl: './admin-policy-documents.css',
})
export class AdminPolicyDocuments implements OnInit {
  loading = false;
  saving = false;
  importing = false;
  deletingId: string | null = null;
  error: string | null = null;

  policyId = '';
  policy: any = null;
  policies: any[] = [];
  items: any[] = [];

  form!: FormGroup;
  importForm!: FormGroup;
  editingId: string | null = null;
  documentPendingDelete: any = null;

  mimeTypeOptions = [
    { label: 'PDF (.pdf)', value: 'application/pdf' },
    { label: 'JPEG (.jpg, .jpeg)', value: 'image/jpeg' },
    { label: 'PNG (.png)', value: 'image/png' },
    { label: 'WEBP (.webp)', value: 'image/webp' },
    { label: 'GIF (.gif)', value: 'image/gif' },
    { label: 'HEIC (.heic)', value: 'image/heic' },
    { label: 'TIFF (.tif, .tiff)', value: 'image/tiff' },
    { label: 'BMP (.bmp)', value: 'image/bmp' },
    { label: 'DOC (.doc)', value: 'application/msword' },
    {
      label: 'DOCX (.docx)',
      value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private adminService: ComplianceAdminService,
    private notifyService: NotificationService,
    private modalCleanup: ModalCleanupService,
  ) { }

  ngOnInit(): void {
    this.policyId = this.route.snapshot.paramMap.get('id') || '';

    this.form = this.fb.group({
      documentTypeCode: ['', [Validators.required, Validators.maxLength(100)]],
      label: ['', [Validators.required, Validators.maxLength(150)]],
      isRequired: [true],
      minFiles: [1, [Validators.required, Validators.min(0)]],
      maxFiles: [1, [Validators.required, Validators.min(1)]],
      requiresFrontAndBack: [false],
      requiresExpiryDate: [false],
      requiresSelfieMatch: [false],
      sortOrder: [0, [Validators.required, Validators.min(0)]],
      acceptedMimeTypesJson: [''],
      acceptedMimeTypes: [[]],
      maxFileSizeMb: [null],
    });

    this.importForm = this.fb.group({
      sourcePolicyId: ['', [Validators.required]],
      overwriteExisting: [false]
    });

    if (!this.policyId) {
      this.error = 'Policy not found.';
      return;
    }

    this.loadPolicy();
    this.loadPolicies();
    this.loadItems();
  }

  loadPolicy(): void {
    this.adminService.getPolicyById(this.policyId).subscribe({
      next: (res: any) => {
        this.policy = res?.data || res || null;
      },
      error: () => {
        this.policy = null;
      }
    });
  }

  loadPolicies(): void {
    this.adminService.getPolicies({ page: 1, pageSize: 1000 }).subscribe({
      next: (res: any) => {
        this.policies = res?.data || res || [];
      },
      error: () => {
        this.policies = [];
      }
    });
  }

  loadItems(): void {
    this.loading = true;
    this.error = null;

    this.adminService.getPolicyDocuments(this.policyId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: any) => {
          this.items = res?.data || res || [];
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load policy documents.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  importDocuments(): void {
    this.importForm.markAllAsTouched();

    if (this.importForm.invalid) return;

    const sourcePolicyId = String(this.importForm.value.sourcePolicyId || '').trim();

    if (!sourcePolicyId || sourcePolicyId === this.policyId) return;

    this.importing = true;
    this.error = null;

    this.adminService.getPolicyDocuments(sourcePolicyId)
      .subscribe({
        next: (res: any) => {
          const sourceDocuments = res?.data || res || [];

          if (!sourceDocuments.length) {
            this.importing = false;
            this.notifyService.showInfo('The selected policy has no documents to import.', 'No Documents');
            return;
          }

          const overwriteExisting = !!this.importForm.value.overwriteExisting;
          const requests = sourceDocuments
            .map((document: any) => this.buildImportRequest(document, overwriteExisting))
            .filter(Boolean);

          if (!requests.length) {
            this.importing = false;
            this.notifyService.showInfo('All selected policy documents already exist on this policy.', 'Nothing Imported');
            return;
          }

          forkJoin(requests)
            .pipe(finalize(() => (this.importing = false)))
            .subscribe({
              next: () => {
                this.notifyService.showSuccess('Policy documents imported successfully.', 'Success');
                this.loadItems();
              },
              error: (err) => {
                this.error = err?.error?.message || 'Failed to import policy documents.';
                this.notifyService.showError(this.error!, 'Error');
              }
            });
        },
        error: (err) => {
          this.importing = false;
          this.error = err?.error?.message || 'Failed to load source policy documents.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  private buildImportRequest(document: any, overwriteExisting: boolean): any {
    const payload = {
      documentTypeCode: String(document.documentTypeCode || '').trim(),
      label: String(document.label || '').trim(),
      isRequired: document.isRequired ?? true,
      minFiles: Number(document.minFiles || 0),
      maxFiles: Number(document.maxFiles || 1),
      requiresFrontAndBack: document.requiresFrontAndBack ?? false,
      requiresExpiryDate: document.requiresExpiryDate ?? false,
      requiresSelfieMatch: document.requiresSelfieMatch ?? false,
      sortOrder: Number(document.sortOrder || 0),
      acceptedMimeTypesJson: this.asNullableString(document.acceptedMimeTypesJson),
      maxFileSizeMb: this.asNullableNumber(document.maxFileSizeMb)
    };

    if (!payload.documentTypeCode || !payload.label) return null;

    const existing = this.items.find(x =>
      String(x.documentTypeCode || '').trim().toUpperCase() === payload.documentTypeCode.toUpperCase()
    );

    if (existing?.id) {
      return overwriteExisting
        ? this.adminService.updatePolicyDocument(this.policyId, existing.id, payload)
        : null;
    }

    return this.adminService.createPolicyDocument(this.policyId, payload);
  }

  save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    this.saving = true;
    this.error = null;

    const payload = {
      documentTypeCode: String(this.form.value.documentTypeCode || '').trim(),
      label: String(this.form.value.label || '').trim(),
      isRequired: !!this.form.value.isRequired,
      minFiles: Number(this.form.value.minFiles || 0),
      maxFiles: Number(this.form.value.maxFiles || 1),
      requiresFrontAndBack: !!this.form.value.requiresFrontAndBack,
      requiresExpiryDate: !!this.form.value.requiresExpiryDate,
      requiresSelfieMatch: !!this.form.value.requiresSelfieMatch,
      sortOrder: Number(this.form.value.sortOrder || 0),
      acceptedMimeTypesJson: this.buildAcceptedMimeTypesJson(),
      maxFileSizeMb: this.asNullableNumber(this.form.value.maxFileSizeMb)
    };

    const request$ = this.editingId
      ? this.adminService.updatePolicyDocument(this.policyId, this.editingId, payload)
      : this.adminService.createPolicyDocument(this.policyId, payload);

    request$
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.notifyService.showSuccess(
            this.editingId ? 'Policy document updated successfully.' : 'Policy document created successfully.',
            'Success'
          );
          this.resetForm();
          this.loadItems();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to save policy document.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  edit(item: any): void {
    this.editingId = item.id;

    this.form.patchValue({
      documentTypeCode: item.documentTypeCode || '',
      label: item.label || '',
      isRequired: item.isRequired ?? true,
      minFiles: item.minFiles ?? 1,
      maxFiles: item.maxFiles ?? 1,
      requiresFrontAndBack: item.requiresFrontAndBack ?? false,
      requiresExpiryDate: item.requiresExpiryDate ?? false,
      requiresSelfieMatch: item.requiresSelfieMatch ?? false,
      sortOrder: item.sortOrder ?? 0,
      acceptedMimeTypesJson: item.acceptedMimeTypesJson || '',
      acceptedMimeTypes: this.parseAcceptedMimeTypes(item.acceptedMimeTypesJson),
      maxFileSizeMb: item.maxFileSizeMb
    });
  }

  openDeleteModal(item: any): void {
    if (!item?.id) return;
    this.documentPendingDelete = item;
  }

  confirmDelete(): void {
    if (!this.documentPendingDelete?.id) return;

    const item = this.documentPendingDelete;
    this.deletingId = item.id;
    this.error = null;

    this.adminService.deletePolicyDocument(this.policyId, item.id)
      .pipe(finalize(() => (this.deletingId = null)))
      .subscribe({
        next: () => {
          this.notifyService.showSuccess('Policy document deleted successfully.', 'Success');
          this.closeDeleteModal();
          this.documentPendingDelete = null;

          if (this.editingId === item.id) {
            this.resetForm();
          }

          this.loadItems();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to delete policy document.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  closeDeleteModal(): void {
    this.modalCleanup.hideById('deletePolicyDocumentModal');
  }

  resetForm(): void {
    this.editingId = null;
    this.form.reset({
      documentTypeCode: '',
      label: '',
      isRequired: true,
      minFiles: 1,
      maxFiles: 1,
      requiresFrontAndBack: false,
      requiresExpiryDate: false,
      requiresSelfieMatch: false,
      sortOrder: 0,
      acceptedMimeTypesJson: '',
      acceptedMimeTypes: [],
      maxFileSizeMb: null
    });
  }

  onAcceptedMimeTypesChanged(values: string[]): void {
    this.form.patchValue({
      acceptedMimeTypesJson: values?.length
        ? JSON.stringify(values)
        : ''
    });
  }

  buildAcceptedMimeTypesJson(): string | null {
    const selected = this.form.value.acceptedMimeTypes || [];

    if (!Array.isArray(selected) || selected.length === 0) {
      return null;
    }

    return JSON.stringify(selected);
  }

  parseAcceptedMimeTypes(value: string | null | undefined): string[] {
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .filter(x => typeof x === 'string')
        .map(x => x.trim())
        .filter(Boolean);
    } catch {
      return [];
    }
  }

  isMimeTypeSelected(value: string): boolean {
    const selected = this.form?.value?.acceptedMimeTypes || [];
    return Array.isArray(selected) && selected.includes(value);
  }

  back(): void {
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'edit']);
  }

  asNullableString(value: any): string | null {
    const text = String(value || '').trim();
    return text ? text : null;
  }

  asNullableNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  openFields(): void {
    if (!this.policyId) return;
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'fields']);
  }

  openDocuments(): void {
    if (!this.policyId) return;
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'documents']);
  }

  openRules(): void {
    if (!this.policyId) return;
    this.router.navigate(['/admin/compliance/policies', this.policyId, 'rules']);
  }

  get availableImportPolicies(): any[] {
    return this.policies.filter(x => String(x.id) !== String(this.policyId));
  }

  get sourcePolicyId() { return this.importForm.get('sourcePolicyId'); }
  get documentTypeCode() { return this.form.get('documentTypeCode'); }
  get label() { return this.form.get('label'); }
}
